/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

const Toast = ({ 
 id,
 type = 'info', 
 title, 
 message, 
 duration = 5000, 
 onClose, 
 position = 'top-right',
 showIcon = true,
 showCloseButton = true,
 className = ''
}) => {
 const [isVisible, setIsVisible] = useState(false);
 const [isLeaving, setIsLeaving] = useState(false);

 useEffect(() => {
 // Trigger entrance animation
 const timer = setTimeout(() => setIsVisible(true), 10);
 return () => clearTimeout(timer);
 }, []);

 useEffect(() => {
 if (duration > 0) {
 const timer = setTimeout(() => {
 handleClose();
 }, duration);
 return () => clearTimeout(timer);
 }
 }, [duration]);

 const handleClose = () => {
 setIsLeaving(true);
 setTimeout(() => {
 onClose?.(id);
 }, 300); // Match animation duration
 };

  const getIcon = () => {
 if (!showIcon) return null;
 
 const iconClass = "h-3.5 w-3.5 flex-shrink-0";
 
 switch (type) {
 case 'success':
 return <CheckCircle className={`${iconClass} text-green-500`} />;
 case 'error':
 return <AlertCircle className={`${iconClass} text-red-500`} />;
 case 'warning':
 return <AlertTriangle className={`${iconClass} text-yellow-500`} />;
 case 'info':
 default:
 return <Info className={`${iconClass} text-ink`} />;
 }
 };

 const getStyles = () => {
 const baseStyles = "theme-vendalist product-toast relative z-[12050] flex items-start rounded-lg border-l-4 p-2.5 shadow-sm transition-all duration-300 ease-in-out transform";
 
 const typeStyles = {
 success: "bg-green-50 border-green-500 text-green-800",
 error: "bg-red-50 border-red-500 text-red-800",
 warning: "bg-yellow-50 border-yellow-500 text-yellow-800",
    info: "bg-surface-muted border-ink text-ink"
 };

 const positionStyles = {
 'top-right': 'translate-x-full',
 'top-left': '-translate-x-full',
 'top-center': '-translate-y-full',
 'bottom-right': 'translate-x-full',
 'bottom-left': '-translate-x-full',
 'bottom-center': 'translate-y-full'
 };

 const animationStyles = isVisible && !isLeaving 
 ? 'translate-x-0 translate-y-0 opacity-100' 
 : positionStyles[position] || 'translate-x-full';

 return `${baseStyles} ${typeStyles[type]} ${animationStyles} ${className}`;
 };

 return (
 <div className={`${getStyles()} ${isLeaving ? 'hidden' : ''}`}>
 {getIcon()}
 
 <div className="ml-2 min-w-0 flex-1">
 {title && (
 <p className="text-[11px] font-medium leading-snug">
 {title}
 </p>
 )}
 {message && (
 <p className={`text-[11px] leading-snug opacity-90 ${title ? 'mt-0.5' : ''}`}>
 {message}
 </p>
 )}
 </div>

 {showCloseButton && (
 <button
 onClick={handleClose}
 className="ml-2 flex-shrink-0 rounded p-0.5 transition-colors duration-200 hover:bg-black hover:bg-opacity-10"
 aria-label="Close notification"
 >
 <X className="h-3.5 w-3.5" />
 </button>
 )}
 </div>
 );
};

// Toast Container Component
export const ToastContainer = ({ toasts, position = 'top-right', onRemove }) => {
 const getContainerStyles = () => {
 // Above ModalShell / lead drawers (z-11000) so success/error alerts stay visible
 const baseStyles = `fixed z-[12050] space-y-2 p-4 max-w-sm w-full ${toasts.length === 0 ? 'hidden' : ''}`;
 
 const positionStyles = {
 'top-right': 'top-4 right-4',
 'top-left': 'top-4 left-4',
 'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
 'bottom-right': 'bottom-4 right-4',
 'bottom-left': 'bottom-4 left-4',
 'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2'
 };

 return `${baseStyles} ${positionStyles[position]}`;
 };

 return (
 <div className={getContainerStyles()}>
 {toasts.map((toast) => (
 <Toast
 key={toast.id}
 {...toast}
 onClose={onRemove}
 position={position}
 />
 ))}
 </div>
 );
};

// Hook for managing toasts
export const useToast = () => {
 const [toasts, setToasts] = useState([]);

 const addToast = (toast) => {
 const id = Date.now() + Math.random();
 const newToast = {
 id,
 type: 'info',
 duration: 5000,
 ...toast
 };
 
 setToasts(prev => [...prev, newToast]);
 return id;
 };

 const removeToast = (id) => {
 setToasts(prev => prev.filter(toast => toast.id !== id));
 };

 const clearAll = () => {
 setToasts([]);
 };

 // Convenience methods
 const success = (title, message, options = {}) => 
 addToast({ type: 'success', title, message, ...options });
 
 const error = (title, message, options = {}) => 
 addToast({ type: 'error', title, message, ...options });
 
 const warning = (title, message, options = {}) => 
 addToast({ type: 'warning', title, message, ...options });
 
 const info = (title, message, options = {}) => 
 addToast({ type: 'info', title, message, ...options });

 return {
 toasts,
 addToast,
 removeToast,
 clearAll,
 success,
 error,
 warning,
 info
 };
};

export default Toast;
